/** 태스크 상태·ID 해석 — 실행 제어 응답과 결과 파일 조회가 공유하는 순수 함수 (P4 분리) */
import { TaskState_Status } from '@/stubs/sm/task_control_pb';
import { SimulationStatus } from '@/stubs/sm/simulation_manager_pb';
import { isInProgressSimStatus, type SimStatus } from '@/types/simulation';

/**
 * 실행 제어 응답의 태스크 상태(문자열) → SimStatus. **이 표가 어휘의 정본이다.**
 *
 * SM 은 어댑터에 상태를 물어 그 enum 이름(TaskState.Status)을 싣고, 어댑터가 응답하지 않으면
 * `UNKNOWN` 을 싣는다(simulation-manager `base_simulation.get_simulation_state`). 키는 그 둘뿐이다 —
 * enum 이 늘면 타입이 이 표를 채우라고 요구한다.
 *
 * 표에 없는 값과 «모름»(UNKNOWN·STATUS_UNSPECIFIED)은 `unknown` 이다. 예전에는 모르는 값을 `running`
 * 으로 단정해, 죽은 참가자(파드 삭제 등)가 일시정지·재개 응답 뒤 «실행 중» 으로 보이고 그 참가자에게
 * 값 조회가 계속 갔다. `unknown` 에서는 조회가 켜지지 않는다(조회는 running·paused 에서만).
 */
const TASK_STATUS_TO_SIM: Readonly<Record<keyof typeof TaskState_Status | 'UNKNOWN', SimStatus>> = {
  STATUS_UNSPECIFIED: 'unknown',
  CREATED: 'building',
  RUNNING: 'running',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  STOPPED: 'stopped',
  UNKNOWN: 'unknown',
};

/**
 * SM 의 실행 상태(proto `vsmr.sm.v1.SimulationStatus` — 세션 스냅샷 `SessionSimulationState.status`·상태 통지
 * `TaskStateChanged.status` 가 싣는다) → SimStatus. **SM 실행 상태의 해석은 이 표 하나다** — 이어보기·재접속 대조·실행 목록·
 * 세션 사건이 이것으로 읽는다. 키가 enum 이라 SM 이 상태를 더하면 tsc 가 이 표를 채우라고 요구한다. 이 판이 모르는 **수**는
 * `unknown`(실행 중으로도 종결로도 단정하지 않는다). BFF 는 JSON 으로 이름을 싣는데, 이 판이 모르는 **이름**(더 새 SM 의 상태)은 이 표에
 * 닿기 전에 스텁의 JSON 해석이 실패한다 — 계약이 어긋났다는 것이 RPC·스트림 오류로 드러난다(웹이 SM·BFF 와 함께 나가야 하는 까닭;
 * 모르는 이름을 0 으로 읽게 하면 «짓는 중» 으로 조용히 잘못 읽는다). 스냅샷에는 종결된 실행(COMPLETED·STOPPED)도 정리 전까지 남아
 * 있다. READY 는 수동 시작 실행이 자원을 세우고 시작을 기다리는 상태라 «짓는 중» 과 같이 읽는다.
 */
const SM_SIM_STATUS_TO_SIM: Readonly<Record<SimulationStatus, SimStatus>> = {
  [SimulationStatus.BUILDING]: 'building',
  [SimulationStatus.READY]: 'building',
  [SimulationStatus.RUNNING]: 'running',
  [SimulationStatus.PAUSED]: 'paused',
  [SimulationStatus.COMPLETED]: 'completed',
  [SimulationStatus.STOPPED]: 'stopped',
};

export function smSimStatusToSimStatus(status: SimulationStatus): SimStatus {
  return Object.prototype.hasOwnProperty.call(SM_SIM_STATUS_TO_SIM, status) ? SM_SIM_STATUS_TO_SIM[status] : 'unknown';
}

/** SM 스냅샷의 실행이 아직 끝나지 않았는가 — 짓는 중·진행 중(`types/simulation` `isInProgressSimStatus`) */
export const smSimInProgress = (status: SimulationStatus): boolean => isInProgressSimStatus(smSimStatusToSimStatus(status));

/** 로그용 SM 실행 상태 이름(이 판이 모르는 수는 그 수) */
export const smSimStatusName = (status: SimulationStatus): string => SimulationStatus[status] ?? String(status);

export function simTaskStateToSimStatus(status: string): SimStatus {
  const key = (status || '').toUpperCase();
  return Object.prototype.hasOwnProperty.call(TASK_STATUS_TO_SIM, key)
    ? TASK_STATUS_TO_SIM[key as keyof typeof TASK_STATUS_TO_SIM]
    : 'unknown';
}

/** SM 의 실행 id — uuid4 를 자르지 않은 16진수 32자(simulation-manager `sm/utils.py` `UID_HEX_LENGTH`). 태스크 id 는
 *  `<실행 id>-<순번>` 이다 */
export const SM_SIM_ID_HEX_LENGTH = 32;

export function parseTaskIndexFromTaskId(taskId?: string | null): number | null {
  const normalized = (taskId || '').trim();
  if (!normalized) {
    return null;
  }

  const lastHyphen = normalized.lastIndexOf('-');
  if (lastHyphen <= 0 || lastHyphen === normalized.length - 1) {
    return null;
  }

  const suffix = normalized.slice(lastHyphen + 1);
  if (!/^\d+$/.test(suffix)) {
    return null;
  }

  const parsed = Number.parseInt(suffix, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

